import CheckIcon from '@mui/icons-material/Check';
import { ErrorBoundary } from '@sentry/react';
import React, { useCallback, useContext } from 'react';

import { ApiContext } from '_lib/api/apiContext';
import { BadRequestError } from '_lib/api/apiErrors';
import {
  CallOutcomeEnum,
  isEmailOutcome,
  isNewAppointmentOutcome,
  isPreviousAppointmentOutcome,
  isRequiredCustomerOutcome,
  isRequiredNotesOutcome,
  isRequiredServiceTypeOutcome,
  isRequiredTradeOutcome,
  Paths,
  PoolBrainCustomerLocationDTO,
  PoolBrainPhoneCallCreateCtxSchema,
  PoolBrainPhoneCallCreateDTO,
  ProviderVersions,
  RequireAtLeastOne,
  SERVICE_TYPES,
  SupportedProviderEnum
} from '_lib/api/apiTypes.shim';
import { CompleteCallWrapper } from '_lib/callPop/common/CompleteCallWrapper/CompleteCallWrapper';
import { useCallLogNavigate } from '_lib/callPop/useCallLogNavigate';
import { Box } from '_lib/components/Box/Box';
import { Button } from '_lib/components/Button/Button';
import { Checkbox } from '_lib/components/Checkbox/Checkbox';
import { DebouncedTextField } from '_lib/components/DebouncedTextField/DebouncedTextField';
import { Divider } from '_lib/components/Divider/Divider';
import { ErrorBoundaryFallback } from '_lib/components/ErrorBoundaryFallback/ErrorBoundaryFallback';

import { ErrorDisplay } from '_lib/components/ErrorDisplay/ErrorDisplay';
import { FlexBox } from '_lib/components/FlexBox/FlexBox';
import { FormControlLabel } from '_lib/components/FormControlLabel/FormControlLabel';
import { DateTimePicker } from '_lib/components/Pickers/DateTimePicker/DateTimePicker';
import { Stack } from '_lib/components/Stack/Stack';

import { Switch } from '_lib/components/Switch/Switch';
import { Typography } from '_lib/components/Typography/Typography';

import { CallDetailsFooter } from '_lib/featureComponents/CallDetailsFooter/CallDetailsFooter';
import { CallOutcomeSelect } from '_lib/featureComponents/CallOutcomeSelect/CallOutcomeSelect';
import { CardBox } from '_lib/featureComponents/CardBox/CardBox';
import { ServiceTypeSelect } from '_lib/featureComponents/ServiceType/ServiceTypeSelect';
import { TradeSelect } from '_lib/featureComponents/Trades/TradeSelect';
import { useAlertDialog } from '_lib/hooks/AlertDialogHook';
import { useConfirmationDialog } from '_lib/hooks/ConfirmationDialogHook';
import { useAuthRoles } from '_lib/hooks/useAuthRoles';
import { useAppDispatch, useAppSelector } from '_lib/redux/hooks';
import { PhoneCallURLState } from '_lib/types/PhoneCall.types';
import { reportException } from '_lib/utils/sentryService';
import { useOurSnackbar } from '_lib/utils/snackUtils';
import { ClearIssueItem } from '_lib/utils/validationErrorUtils';

import { zipZoneDialogCheck } from '_lib/utils/zipZoneDialogUtils';

import { PoolBrainCompanyContext, selectZoneZipContext } from '../../../../common/CompanyPane/Company.slice';

import {
  initialize,
  PhoneCallContext,
  selectPhoneCallContext,
  selectPhoneCallFieldIssues,
  selectPhoneCallNonFieldErrors,
  setPartialPhoneCallContext,
  setPhoneCallFieldIssues,
  setPhoneCallNonFieldErrors
} from '../../PoolBrainPhoneCall.slice';

import { CustomerLocationCard } from './CustomerLocationCard/CustomerLocationCard';

type PoolBrainCallDetailsProps = {
  companyContext: PoolBrainCompanyContext;
  phoneCallURLState: PhoneCallURLState;
  aiCallId?: string;
  onSetIsSaving: (isSaving: boolean) => void;
  showFooter?: boolean;
};

export const handledIssues: Pick<
  { [k in Paths<PoolBrainPhoneCallCreateDTO>]: k },
  | 'notes'
  | 'internalNotes'
  | 'appointmentDate'
  | 'previousAppointmentDateTime'
  | 'outcome'
  | 'serviceType'
  | 'trade'
  // TODO - need to discuss providerData customer AND location errors
  //      - need to gracefully handle them all (unexpected PB data)
  //      - might have to get creative with display. IE: 'providerData.customer.email' would need to indicate Customer email is invalid
  // NOTE - customer parse errors will be handled in the CustomerLocationCard, but all location errors are not directly handled,
  //        so need to live in 'nonFieldErrors' or 'extraErrors' for now to be displayed
  | 'providerData.customer'
> = {
  notes: 'notes',
  internalNotes: 'internalNotes',
  appointmentDate: 'appointmentDate',
  previousAppointmentDateTime: 'previousAppointmentDateTime',
  outcome: 'outcome',
  serviceType: 'serviceType',
  trade: 'trade',
  'providerData.customer': 'providerData.customer'
} as const;

const toProviderData = (
  customerLocation: PoolBrainCustomerLocationDTO | null
): PoolBrainPhoneCallCreateDTO['providerData'] => {
  if (!customerLocation) return null;
  return {
    customer: customerLocation.customer,
    location: customerLocation
  };
};

export const PoolBrainCallDetails = (props: PoolBrainCallDetailsProps) => {
  const { companyContext, phoneCallURLState, onSetIsSaving, showFooter, aiCallId } = props;

  const api = useContext(ApiContext);
  const { navigateToCallLog } = useCallLogNavigate();
  const { enqueueSnackbar } = useOurSnackbar();
  const authRoles = useAuthRoles();

  const confirmationDialog = useConfirmationDialog();
  const alertDialog = useAlertDialog();

  const phoneCallContext = useAppSelector(selectPhoneCallContext);
  const fieldIssues = useAppSelector(selectPhoneCallFieldIssues);
  const nonFieldErrors = useAppSelector(selectPhoneCallNonFieldErrors);

  const zoneZipContext = useAppSelector(selectZoneZipContext);

  const dispatch = useAppDispatch();

  const handlePhoneCallContextUpdate = useCallback(
    (updates: RequireAtLeastOne<Partial<PhoneCallContext>>, clearIssues?: ClearIssueItem[]) => {
      dispatch(setPartialPhoneCallContext({ updates, clearIssues }));
    },
    [dispatch]
  );

  const handleSetCallOutcome = async (callOutcome: CallOutcomeEnum) => {
    const phoneCallContextUpdates: RequireAtLeastOne<Partial<PhoneCallContext>> = {
      callOutcome
    };

    if (
      isEmailOutcome(callOutcome) ||
      ![
        CallOutcomeEnum.CallDropped,
        CallOutcomeEnum.NoCallerOnLine,
        CallOutcomeEnum.Telemarketer,
        CallOutcomeEnum.WrongNumber,
        CallOutcomeEnum.NotPropertyOwner
      ].includes(callOutcome)
    ) {
      phoneCallContextUpdates.sendCompanyEmail = true;
    }

    if ([CallOutcomeEnum.Cancellation, CallOutcomeEnum.UpdateNotes].includes(callOutcome)) {
      phoneCallContextUpdates.appointmentDateTime = null;
    }

    // reset any error state based on setting outcome:
    const clearIssues: ClearIssueItem[] = [handledIssues.outcome];

    if (!isNewAppointmentOutcome(callOutcome)) {
      clearIssues.push(handledIssues['appointmentDate']);
    }

    if (!isPreviousAppointmentOutcome(callOutcome)) {
      clearIssues.push(handledIssues['previousAppointmentDateTime']);
    }

    if (!isRequiredCustomerOutcome(callOutcome)) {
      clearIssues.push(handledIssues['providerData.customer']);
    }

    handlePhoneCallContextUpdate(phoneCallContextUpdates, clearIssues);
  };

  const completeCall = async (data: PoolBrainPhoneCallCreateDTO) => {
    try {
      onSetIsSaving(true);
      await api.poolBrainPhoneCalls.create(data);
      navigateToCallLog();
    } catch (err) {
      // If we somehow pass validation client side and fail on the api, just dump it (for now)
      if (err instanceof BadRequestError) {
        dispatch(setPhoneCallNonFieldErrors(err.responseData.nonFieldErrors));
        dispatch(
          setPhoneCallFieldIssues(
            err.responseData.fieldIssues.filter((issue) => issue.path?.[0] !== 'nonFieldErrors') || []
          )
        );
      }
      reportException(err);
    } finally {
      onSetIsSaving(false);
    }
  };

  const handleCompleteCall = async () => {
    dispatch(setPhoneCallFieldIssues([]));
    dispatch(setPhoneCallNonFieldErrors([]));

    if (!phoneCallContext.callOutcome) {
      enqueueSnackbar('Call Outcome is Required');
      return;
    }
    const company = companyContext.company;

    const result = PoolBrainPhoneCallCreateCtxSchema(company).safeParse({
      id: phoneCallURLState.phoneCallId,
      // General props
      providerType: SupportedProviderEnum.POOL_BRAIN,
      providerVersion: ProviderVersions.v1,
      callerId: phoneCallURLState.callerId,
      duration: Date.now() - phoneCallURLState.durationStart,
      internalNotes: phoneCallContext.internalNotes || null,
      isManualCall: phoneCallURLState.isManualCall,
      notes: phoneCallContext.notes || null,
      outcome: phoneCallContext.callOutcome,
      sendCompanyEmail: phoneCallContext.sendCompanyEmail,
      sendEscalationEmail: phoneCallContext.sendEscalationEmail,
      trade: phoneCallContext.trade || null,
      serviceType: phoneCallContext.serviceType || null,
      telephonyId: phoneCallURLState.telephonyId || null,
      aiCallId: aiCallId || null,

      // Company Props
      companyId: company.id,
      companyDNIS: phoneCallURLState.companyDNIS,

      // Zones
      companyZoneId: zoneZipContext?.selectedZone?.id || null,

      // Sister switching
      companySwitched: companyContext.switched,
      originalCompany: companyContext.originalCompanyName || null,

      // Appointment props
      appointmentDate: phoneCallContext.appointmentDateTime,
      previousAppointmentDateTime:
        phoneCallContext.callOutcome === CallOutcomeEnum.Reschedule
          ? phoneCallContext.previousAppointmentDateTime
          : null,

      providerData: toProviderData(phoneCallContext.customerLocation),
      _fieldOverrides: {
        previousAppointmentDateTime: phoneCallContext.overridePreviousAppointmentDateTime
      }
    });

    if (result.success && result.data) {
      if (!company.isZoned) return completeCall(result.data);

      await zipZoneDialogCheck({
        companyContext,
        selectedZone: zoneZipContext?.selectedZone || null,
        zipOrPostal: phoneCallContext.customerLocation?.zipOrPostal,
        confirmationCallback: () => completeCall(result.data),
        confirmationDialog,
        alertDialog
      });
    } else if (!result.success) {
      dispatch(setPhoneCallFieldIssues(result.error.issues.filter((issue) => issue.path?.[0] !== 'nonFieldErrors')));

      const handledIssueValues = Object.values(handledIssues);
      const extraErrors: string[] = result.error.issues
        .filter(({ path }) => !handledIssueValues.find((issue) => path.join('.').includes(issue)))
        .map(({ message }) => message);

      if (extraErrors.length) dispatch(setPhoneCallNonFieldErrors(extraErrors));

      enqueueSnackbar('Phone Call is Invalid!');
    }
  };

  const reInitCallDetails = () => {
    dispatch(initialize({ company: companyContext.company, options: { keepExistingCallData: true } }));
  };

  return (
    <ErrorBoundary
      beforeCapture={(scope) => {
        scope.setTag('pane', 'PoolBrain Call Details');
      }}
      fallback={({ resetError }) => (
        <ErrorBoundaryFallback
          title='Error Displaying Call Details'
          onRefresh={() => {
            reInitCallDetails();
            resetError();
          }}
        />
      )}>
      <FlexBox
        sx={(theme) => ({
          width: '100%',
          flexDirection: 'column',
          backgroundColor: theme.palette.callPane.main,
          color: theme.palette.callPane.contrastText
        })}>
        <Stack
          direction='row'
          sx={{ justifyContent: 'space-between', alignItems: 'center', paddingX: 1.5, paddingY: 2 }}>
          <Typography variant='h4' text='CALL DETAILS' />
        </Stack>
        <Stack sx={{ paddingX: 1.5, paddingBottom: 1.5, overflow: 'auto', position: 'relative', flexGrow: 1 }}>
          {!!nonFieldErrors.length && <ErrorDisplay errors={nonFieldErrors} sx={{ borderRadius: 1 }} />}
          <CardBox>
            <CallOutcomeSelect
              callOutcome={phoneCallContext?.callOutcome}
              issuePath={handledIssues.outcome}
              issues={fieldIssues}
              onSetCallOutcome={handleSetCallOutcome}
            />
          </CardBox>
          {phoneCallContext?.customerLocation && (
            <CustomerLocationCard
              customerLocation={phoneCallContext.customerLocation}
              companyId={companyContext.company.id}
              handleSetCustomerLocation={(customerLocation) =>
                handlePhoneCallContextUpdate({ customerLocation }, [handledIssues['providerData.customer']])
              }
            />
          )}
          {isNewAppointmentOutcome(phoneCallContext.callOutcome) && (
            <CardBox>
              <DateTimePicker
                clearable
                disablePast
                skipDisabled
                label='Requested Appointment Date'
                value={phoneCallContext.appointmentDateTime ? new Date(phoneCallContext.appointmentDateTime) : null}
                textFieldProps={{
                  error: {
                    issues: fieldIssues,
                    issuePath: handledIssues.appointmentDate
                  },
                  required: true
                }}
                timeSteps={{ minutes: 30 }}
                timezone={companyContext.company.timezone}
                onChange={(value: Date | null) => {
                  handlePhoneCallContextUpdate(
                    { appointmentDateTime: value && value.toISOString() },
                    value ? [handledIssues.appointmentDate] : undefined
                  );
                }}
              />
            </CardBox>
          )}
          {isPreviousAppointmentOutcome(phoneCallContext.callOutcome) && (
            <CardBox>
              <DateTimePicker
                label='Previous Appointment Date'
                onChange={(date) =>
                  handlePhoneCallContextUpdate({ previousAppointmentDateTime: date ? date.toISOString() : null }, [
                    handledIssues.previousAppointmentDateTime
                  ])
                }
                textFieldProps={{
                  required: phoneCallContext.overridePreviousAppointmentDateTime ? false : true,
                  error: { issuePath: handledIssues.previousAppointmentDateTime, issues: fieldIssues }
                }}
                timeSteps={{ minutes: 30 }}
                timezone={companyContext.company.timezone}
                value={phoneCallContext.previousAppointmentDateTime}
              />
              <FormControlLabel
                control={
                  <Switch
                    color='primary'
                    checked={phoneCallContext.overridePreviousAppointmentDateTime}
                    onChange={(e) => {
                      handlePhoneCallContextUpdate(
                        { overridePreviousAppointmentDateTime: e.target.checked },
                        e.target.checked ? [handledIssues.previousAppointmentDateTime] : undefined
                      );
                    }}
                  />
                }
                label={<Typography text='Override Field Requirements' />}
              />
            </CardBox>
          )}
          {isRequiredTradeOutcome(phoneCallContext.callOutcome) && (
            <CardBox>
              <TradeSelect
                trade={phoneCallContext.trade}
                onSetTrade={(trade) => {
                  handlePhoneCallContextUpdate({ trade }, [handledIssues.trade]);
                }}
                trades={companyContext.company.callCenter.trades}
                issuePath={handledIssues.trade}
                issues={fieldIssues}
              />
            </CardBox>
          )}
          {isRequiredServiceTypeOutcome(phoneCallContext.callOutcome) && (
            <CardBox>
              <ServiceTypeSelect
                serviceType={phoneCallContext.serviceType}
                onSetServiceType={(serviceType) => {
                  handlePhoneCallContextUpdate({ serviceType }, [handledIssues.serviceType]);
                }}
                serviceTypes={SERVICE_TYPES}
                issuePath={handledIssues.serviceType}
                issues={fieldIssues}
              />
            </CardBox>
          )}
          <CardBox>
            <DebouncedTextField
              fullWidth
              label='Notes'
              required={isRequiredNotesOutcome(phoneCallContext.callOutcome)}
              multiline
              error={{
                issuePath: handledIssues.notes,
                issues: fieldIssues
              }}
              debounceOptions={{ initialValue: phoneCallContext.notes }}
              onChangeHandler={(notes: string) => {
                handlePhoneCallContextUpdate({ notes }, notes ? [handledIssues.notes] : undefined);
              }}
            />
          </CardBox>
          <CardBox>
            <FormControlLabel
              control={
                <Checkbox
                  color='primary'
                  disabled={isEmailOutcome(phoneCallContext.callOutcome)}
                  checked={phoneCallContext.sendCompanyEmail}
                  onChange={(e) => handlePhoneCallContextUpdate({ sendCompanyEmail: !!e.target.checked })}
                />
              }
              label={<Typography text='Send Company Email' />}
            />
          </CardBox>
          <CardBox>
            <DebouncedTextField
              fullWidth
              label='Call Center Internal Notes'
              multiline
              required={phoneCallContext.sendEscalationEmail}
              error={{
                issuePath: handledIssues.internalNotes,
                issues: fieldIssues
              }}
              debounceOptions={{ initialValue: phoneCallContext.internalNotes }}
              onChangeHandler={(internalNotes: string) => {
                handlePhoneCallContextUpdate(
                  { internalNotes },
                  internalNotes ? [handledIssues.internalNotes] : undefined
                );
              }}
            />
          </CardBox>
          {!authRoles.isAccountUser && !authRoles.isAccountAdmin && (
            <CardBox>
              <FormControlLabel
                control={
                  <Checkbox
                    color='primary'
                    checked={phoneCallContext.sendEscalationEmail}
                    onChange={(e) =>
                      handlePhoneCallContextUpdate(
                        { sendEscalationEmail: !!e.target.checked },
                        !e.target.checked ? [handledIssues.internalNotes] : undefined
                      )
                    }
                  />
                }
                label={<Typography text='Send Escalation Email' />}
              />
            </CardBox>
          )}
          <CardBox>
            <FormControlLabel
              control={<Checkbox color='primary' disabled checked={companyContext.switched} />}
              label={<Typography text='Company Switched' />}
            />
          </CardBox>
          <Box>
            <CompleteCallWrapper nonFieldErrors={nonFieldErrors}>
              <Button
                fullWidth
                onClick={handleCompleteCall}
                startIcon={<CheckIcon />}
                color='success'
                variant='contained'>
                Complete Call
              </Button>
            </CompleteCallWrapper>
          </Box>
        </Stack>
        <Divider />
        {showFooter && <CallDetailsFooter />}
      </FlexBox>
      {confirmationDialog.element}
      {alertDialog.element}
    </ErrorBoundary>
  );
};
